import React, { useState, useEffect, useCallback } from 'react';
import { usePageMeta } from '../hooks/usePageMeta';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { Code, Lock, User, Mail, Building2, Briefcase, AlertCircle, CheckCircle2, Book, Key, Zap, Shield } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DevelopersPage = () => {
  usePageMeta('Developer Portal API Spedizioni | SpedireOnline', 'Portale sviluppatori SpedireOnline: catalogo e documentazione delle API di logistica (spedizioni, ritiri, tracking, TARIC). Integra le API corriere nella tua applicazione.');
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [developer, setDeveloper] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const [registerForm, setRegisterForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    role: '',
    password: '',
    confirmPassword: '',
    acceptTerms: false
  });

  const checkAuthentication = useCallback(async (token) => {
    try {
      const response = await axios.get(`${API}/developers/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.is_approved) {
        setDeveloper(response.data);
        setIsAuthenticated(true);
      }
    } catch (error) {
      localStorage.removeItem('dev_token');
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('dev_token');
    if (token) {
      checkAuthentication(token);
    }
  }, [checkAuthentication]);

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (registerForm.password !== registerForm.confirmPassword) {
      toast.error('Le password non coincidono');
      return;
    }
    
    if (!registerForm.acceptTerms) {
      toast.error('Devi accettare i Termini e la Privacy Policy');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/developers/register`, {
        first_name: registerForm.firstName,
        last_name: registerForm.lastName,
        email: registerForm.email,
        company: registerForm.company,
        role: registerForm.role,
        password: registerForm.password
      });
      
      if (response.data.success) {
        toast.success('Registrazione completata! Il tuo account è in attesa di approvazione. Riceverai una email quando sarà attivato.');
        setRegisterForm({
          firstName: '',
          lastName: '',
          email: '',
          company: '',
          role: '',
          password: '',
          confirmPassword: '',
          acceptTerms: false
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante la registrazione');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('dev_token');
    setIsAuthenticated(false);
    setDeveloper(null);
    toast.success('Logout effettuato');
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-primary)' }}>
      {/* Header */}
      <nav className="nav-bar">
        <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
          <img 
            src="https://customer-assets.emergentagent.com/job_ship-smart-6/artifacts/4h2rj0xf_logoonly.png" 
            alt="SpedireOnline.net" 
            className="h-8 w-8 object-contain"
          />
          <span className="font-bold text-xl" style={{ color: 'var(--brand-primary)' }}>spedireonline.net</span>
        </div>
        <div className="flex items-center gap-4">
          <a href="/" className="text-sm" style={{ color: 'var(--text-secondary)' }}>Home</a>
          {isAuthenticated ? (
            <>
              <Button onClick={() => navigate('/documentazione-api')} variant="outline" size="sm">
                Documentazione API
              </Button>
              <Button onClick={() => navigate('/developer-dashboard')} variant="outline" size="sm">
                Dashboard
              </Button>
              <Button onClick={handleLogout} variant="outline" size="sm">Logout</Button>
            </>
          ) : (
            <Button onClick={() => navigate('/developer-auth')} variant="outline" size="sm">Login</Button>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-20 px-6" style={{ background: 'linear-gradient(135deg, #003542 0%, #005766 100%)' }}>
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-6" style={{ background: 'rgba(158, 197, 82, 0.2)' }}>
            <Code className="w-10 h-10" style={{ color: 'var(--accent-primary)' }} />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-6 text-white">
            API per Sviluppatori
          </h1>
          <p className="text-xl text-gray-200 mb-8">
            Integra le nostre potenti API nella tua applicazione per validazione indirizzi,
            geocoding, e molto altro
          </p>
          <div className="flex gap-4 justify-center">
            {isAuthenticated ? (
              <>
                <Button onClick={() => navigate('/documentazione-api')} className="btn-primary" size="lg">
                  <Book className="w-5 h-5 mr-2" />
                  Vai alla Documentazione
                </Button>
                <Button onClick={() => navigate('/developer-dashboard')} variant="outline" size="lg" className="bg-white">
                  Dashboard
                </Button>
              </>
            ) : (
              <>
                <Button onClick={() => {
                  document.getElementById('registration')?.scrollIntoView({ behavior: 'smooth' });
                }} className="btn-primary" size="lg">
                  Inizia Ora - Gratis
                </Button>
                <Button onClick={() => navigate('/developer-auth')} variant="outline" size="lg" className="bg-white">
                  Accedi
                </Button>
              </>
            )}
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
              Cosa Offriamo
            </h2>
            <p className="text-lg" style={{ color: 'var(--text-secondary)' }}>
              API RESTful moderne e facili da integrare con documentazione completa
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 rounded-full mb-4 flex items-center justify-center" style={{ background: 'var(--accent-wash)' }}>
                  <CheckCircle2 className="w-6 h-6" style={{ color: 'var(--accent-primary)' }} />
                </div>
                <CardTitle>Validazione Indirizzi</CardTitle>
              </CardHeader>
              <CardContent>
                <p style={{ color: 'var(--text-secondary)' }}>
                  API per validare e verificare indirizzi italiani con supporto multi-provider (GLS, FedEx) e geocoding preciso.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 rounded-full mb-4 flex items-center justify-center" style={{ background: 'var(--accent-wash)' }}>
                  <Zap className="w-6 h-6" style={{ color: 'var(--accent-primary)' }} />
                </div>
                <CardTitle>Prestazioni Elevate</CardTitle>
              </CardHeader>
              <CardContent>
                <p style={{ color: 'var(--text-secondary)' }}>
                  Risposte rapide con rate limiting ottimizzato. Infrastruttura scalabile per gestire migliaia di richieste.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 rounded-full mb-4 flex items-center justify-center" style={{ background: 'var(--accent-wash)' }}>
                  <Shield className="w-6 h-6" style={{ color: 'var(--accent-primary)' }} />
                </div>
                <CardTitle>Sicurezza Garantita</CardTitle>
              </CardHeader>
              <CardContent>
                <p style={{ color: 'var(--text-secondary)' }}>
                  Autenticazione sicura con API Keys, HTTPS obbligatorio, e monitoraggio attivo per proteggere i tuoi dati.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 px-6" style={{ background: 'var(--bg-section)' }}>
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
              Come Funziona
            </h2>
            <p className="text-lg" style={{ color: 'var(--text-secondary)' }}>
              Inizia a usare le nostre API in 3 semplici step
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center text-2xl font-bold" style={{ background: 'var(--accent-primary)', color: 'white' }}>
                1
              </div>
              <h3 className="text-xl font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
                Registrati
              </h3>
              <p style={{ color: 'var(--text-secondary)' }}>
                Crea il tuo account sviluppatore compilando il form di registrazione
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center text-2xl font-bold" style={{ background: 'var(--accent-primary)', color: 'white' }}>
                2
              </div>
              <h3 className="text-xl font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
                Ottieni Approvazione
              </h3>
              <p style={{ color: 'var(--text-secondary)' }}>
                Il nostro team approva il tuo account e ricevi un'email di conferma
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center text-2xl font-bold" style={{ background: 'var(--accent-primary)', color: 'white' }}>
                3
              </div>
              <h3 className="text-xl font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
                Inizia a Sviluppare
              </h3>
              <p style={{ color: 'var(--text-secondary)' }}>
                Accedi alla documentazione completa e integra le API nella tua app
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Registration Section */}
      {!isAuthenticated && (
        <section id="registration" className="py-16 px-6">
          <div className="max-w-3xl mx-auto">
            <Card className="border-2" style={{ borderColor: 'var(--accent-primary)' }}>
              <CardHeader>
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-3 rounded-full" style={{ background: 'var(--accent-wash)' }}>
                    <Lock className="w-6 h-6" style={{ color: 'var(--accent-primary)' }} />
                  </div>
                  <div>
                    <CardTitle className="text-2xl">Registrazione Sviluppatore</CardTitle>
                    <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                      Compila il form per richiedere l'accesso alle API
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-800">
                      <p className="font-semibold mb-1">Processo di Approvazione:</p>
                      <ol className="list-decimal list-inside space-y-1">
                        <li>Compila il form di registrazione</li>
                        <li>Attendi l'approvazione dell'amministratore</li>
                        <li>Ricevi email di conferma</li>
                        <li>Accedi e usa le API</li>
                      </ol>
                    </div>
                  </div>
                </div>

                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        <User className="w-4 h-4 inline mr-2" />
                        Nome *
                      </label>
                      <Input
                        type="text"
                        value={registerForm.firstName}
                        onChange={(e) => setRegisterForm({ ...registerForm, firstName: e.target.value })}
                        required
                        placeholder="Mario"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Cognome *</label>
                      <Input
                        type="text"
                        value={registerForm.lastName}
                        onChange={(e) => setRegisterForm({ ...registerForm, lastName: e.target.value })}
                        required
                        placeholder="Rossi"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      <Mail className="w-4 h-4 inline mr-2" />
                      Email *
                    </label>
                    <Input
                      type="email"
                      value={registerForm.email}
                      onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                      required
                      placeholder="mario.rossi@azienda.it"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        <Building2 className="w-4 h-4 inline mr-2" />
                        Azienda *
                      </label>
                      <Input
                        type="text"
                        value={registerForm.company}
                        onChange={(e) => setRegisterForm({ ...registerForm, company: e.target.value })}
                        required
                        placeholder="Nome azienda"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        <Briefcase className="w-4 h-4 inline mr-2" />
                        Ruolo *
                      </label>
                      <Input
                        type="text"
                        value={registerForm.role}
                        onChange={(e) => setRegisterForm({ ...registerForm, role: e.target.value })}
                        required
                        placeholder="es. Sviluppatore Backend"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Password *</label>
                      <Input
                        type="password"
                        value={registerForm.password}
                        onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                        required
                        minLength={8}
                        placeholder="Minimo 8 caratteri"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Conferma Password *</label>
                      <Input
                        type="password"
                        value={registerForm.confirmPassword}
                        onChange={(e) => setRegisterForm({ ...registerForm, confirmPassword: e.target.value })}
                        required
                        placeholder="Conferma password"
                      />
                    </div>
                  </div>

                  <div className="flex items-start gap-2 pt-2">
                    <Checkbox
                      id="terms"
                      checked={registerForm.acceptTerms}
                      onCheckedChange={(checked) => setRegisterForm({ ...registerForm, acceptTerms: checked })}
                    />
                    <label htmlFor="terms" className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                      Accetto i{' '}
                      <a href="https://www.iubenda.com/privacy-policy/99691573" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: 'var(--accent-primary)' }}>
                        Termini di Servizio
                      </a>
                      {' '}e la{' '}
                      <a href="https://www.iubenda.com/privacy-policy/99691573" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: 'var(--accent-primary)' }}>
                        Privacy Policy
                      </a>
                    </label>
                  </div>

                  <Button type="submit" className="w-full btn-primary" disabled={loading}>
                    {loading ? 'Registrazione in corso...' : 'Richiedi Accesso'}
                  </Button>

                  <p className="text-sm text-center" style={{ color: 'var(--text-muted)' }}>
                    Hai già un account? <a href="/developer-auth" className="underline font-medium" style={{ color: 'var(--accent-primary)' }}>Accedi qui</a>
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        </section>
      )}

      {/* CTA Section */}
      {isAuthenticated && (
        <section className="py-16 px-6" style={{ background: 'var(--accent-wash)' }}>
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
              Pronto per Iniziare?
            </h2>
            <p className="text-lg mb-8" style={{ color: 'var(--text-secondary)' }}>
              Accedi alla documentazione completa e inizia a integrare le nostre API
            </p>
            <div className="flex gap-4 justify-center">
              <Button onClick={() => navigate('/documentazione-api')} className="btn-primary" size="lg">
                <Book className="w-5 h-5 mr-2" />
                Documentazione API
              </Button>
              <Button onClick={() => navigate('/developer-dashboard')} variant="outline" size="lg">
                <Key className="w-5 h-5 mr-2" />
                Dashboard
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer style={{ background: 'var(--bg-section)' }} className="py-12 px-6 border-t">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center gap-2 mb-4 md:mb-0">
              <img 
                src="https://customer-assets.emergentagent.com/job_ship-smart-6/artifacts/4h2rj0xf_logoonly.png" 
                alt="SpedireOnline.net" 
                className="h-8 w-8 object-contain"
              />
              <span className="font-bold" style={{ color: 'var(--brand-primary)' }}>spedireonline.net</span>
            </div>
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
              © 2024 Business Service Srl. Tutti i diritti riservati.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DevelopersPage;
